import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LEAD_RECORD_SEARCH_LIMIT,
  LEAD_RECORD_SEARCH_MAX,
  LEAD_RECORD_SEARCH_MIN,
  LEAD_RECORD_SEARCH_UNAVAILABLE_MESSAGE,
  getLeadErrorMessage,
  readApiErrorCode,
  readLeadRecordSearch,
  searchLeadRecords,
} from '../../services/leadsService';

export const LEAD_RECORD_SEARCH_DEBOUNCE_MS = 300;

// Siempre `include=lists` (M3): el desplegable muestra en qué listas está cada registro.
const activityQuery = (hasActivity) => ({
  includeLists: true,
  ...(typeof hasActivity === 'boolean' ? { hasActivity } : {}),
});

const kindsQuery = (kindsKey) => (kindsKey ? { itemKinds: kindsKey.split(',') } : {});

const IDLE = { status: 'idle', groups: [], hasMore: false, nextOffset: null, truncated: false, error: '' };

function errorOf(res) {
  if (res?._httpStatus === 503 || readApiErrorCode(res) === 'SEARCH_UNAVAILABLE') {
    return { status: 'unavailable', error: LEAD_RECORD_SEARCH_UNAVAILABLE_MESSAGE };
  }
  if (res?._httpStatus === 403) return { status: 'error', error: 'No tenés acceso a los registros de este espacio.' };
  return { status: 'error', error: getLeadErrorMessage(res, 'No se pudo buscar.') };
}

/**
 * Buscador de registros con debounce (~300 ms) y mínimo de 2 caracteres.
 * Cada texto nuevo cancela el pedido anterior. `loadMore` pide la página
 * siguiente (`next_offset`) y la suma.
 *
 * `hasActivity`: por defecto el del servicio (`LEAD_RECORD_SEARCH_ACTIVITY_ONLY`,
 * filtro del panel). La carga manual de consultas pasa `false` (todos los
 * registros propios, tengan o no consultas).
 *
 * @param {string} text Lo que escribió el usuario.
 * `itemKinds` (opcional): `item_kind` del pedido (solo esos tipos matchean). Sin él, todos.
 *
 * @param {{ ownerUserId?: string|null, debounceMs?: number, hasActivity?: boolean, itemKinds?: string[]|null }} [options]
 */
export default function useLeadRecordSearch(text, {
  ownerUserId = null,
  debounceMs = LEAD_RECORD_SEARCH_DEBOUNCE_MS,
  hasActivity = undefined,
  itemKinds = null,
} = {}) {
  // Clave estable para los efectos (el array puede llegar nuevo en cada render).
  const kindsKey = Array.isArray(itemKinds) && itemKinds.length ? itemKinds.join(',') : '';
  const [query, setQuery] = useState('');
  const [state, setState] = useState(IDLE);
  const [loadingMore, setLoadingMore] = useState(false);
  const moreAbortRef = useRef(null);

  useEffect(() => {
    const next = String(text || '').trim().slice(0, LEAD_RECORD_SEARCH_MAX);
    const handle = setTimeout(() => setQuery(next.length >= LEAD_RECORD_SEARCH_MIN ? next : ''), debounceMs);
    return () => clearTimeout(handle);
  }, [text, debounceMs]);

  useEffect(() => {
    moreAbortRef.current?.abort();
    setLoadingMore(false);
    if (!query) {
      setState(IDLE);
      return undefined;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    (async () => {
      try {
        const res = await searchLeadRecords(
          { q: query, limit: LEAD_RECORD_SEARCH_LIMIT, ...activityQuery(hasActivity), ...kindsQuery(kindsKey) },
          { ownerUserId, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (!res?._httpOk) {
          setState({ ...IDLE, ...errorOf(res) });
          return;
        }
        setState({ status: 'ready', error: '', ...readLeadRecordSearch(res) });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setState({ ...IDLE, status: 'error', error: 'No se pudo buscar.' });
      }
    })();
    return () => controller.abort();
  }, [query, ownerUserId, hasActivity, kindsKey]);

  useEffect(() => () => moreAbortRef.current?.abort(), []);

  const loadMore = useCallback(async () => {
    if (state.status !== 'ready' || !state.hasMore || state.nextOffset === null || loadingMore) return;
    const controller = new AbortController();
    moreAbortRef.current = controller;
    setLoadingMore(true);
    try {
      const res = await searchLeadRecords(
        { q: query, limit: LEAD_RECORD_SEARCH_LIMIT, offset: state.nextOffset, ...activityQuery(hasActivity), ...kindsQuery(kindsKey) },
        { ownerUserId, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (!res?._httpOk) {
        setState((prev) => ({ ...prev, hasMore: false }));
        return;
      }
      const page = readLeadRecordSearch(res);
      setState((prev) => {
        const seen = new Set(prev.groups.map((g) => `${g.itemKind}:${g.id}`));
        return {
          ...prev,
          groups: [...prev.groups, ...page.groups.filter((g) => !seen.has(`${g.itemKind}:${g.id}`))],
          hasMore: page.hasMore,
          nextOffset: page.nextOffset,
          truncated: page.truncated,
        };
      });
    } catch (err) {
      if (err?.name !== 'AbortError') setState((prev) => ({ ...prev, hasMore: false }));
    } finally {
      if (!controller.signal.aborted) setLoadingMore(false);
    }
  }, [state, loadingMore, query, ownerUserId, hasActivity, kindsKey]);

  return { ...state, query, loadingMore, loadMore };
}
